




// Vue
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Mixins } from 'vue-mixin-decorator';
import { mapGetters, mapActions } from 'vuex';
import Endpoints from '@/constants/endpoints';

// Third party
import dayjs, { UnitType } from 'dayjs';
import 'dayjs/locale/es';

// Constants
import Assets from '@/constants/assets.ts';
import Constants from '@/constants/constants.ts';
import RouteNames from '@/constants/route-names';

// Components
import Btn, { BtnColor, BtnWidth } from '@/shared/components/btn.component.vue';
import ConvocationRow from '@/manager/convocation/views/convocationDetail/convocationRow/convocationRow.view.vue';
import DropUp, { DropUpColorHover } from '@/shared/components/DropUp.component.vue';
import GroupFilter from '@/shared/components/GroupFilter.component.vue';
import Icon, { IconEnum } from '@/shared/components/icon.component.vue';
import Pagination from '@/shared/components/Pagination/Pagination.component.vue';
import ItemThumb from '@/shared/components/item-thumb.component.vue';
import Spinner from '@/shared/components/Spinner.component.vue';
import TableColumnOrder from '@/shared/components/TableColumnOrder.component.vue';
import Modal, { ModalTitleColor, ModalWidth } from '@/shared/components/Modal.component.vue';

// Models - Interfaces
import { CounterColor } from '@/shared/components/counter/counter.enum';
import { CounterInterface } from '@/shared/components/counter/counter.interface';
import { Participant } from '@/manager/convocation/participant.model';
import { Convocation } from '@/manager/convocation/convocation.model';
import { SecondLevelOptionModel } from '@/shared/models/secondLevelOption.model';
import { TimetablesInterface } from '@/shared/interfaces/timetables.interface';

// Services
import { ConvocationService } from '@/manager/convocation/convocation.service';

// Mixins
import WithSpinner from '@/shared/mixins/withSpinner.mixin.ts';
import WithPagination from '@/shared/mixins/withPagination.mixin.ts';
import ColumnOrderMixin from '@/shared/mixins/columnOrder.mixin.ts';
import SearchMixin from '@/shared/mixins/search.mixin.ts';

export enum ListActions {
  DownloadCsv = 'download-csv',
  EmailToMultiple = 'email-to-multiple',
  CountDown = 'count-down',
  ToPurchases = 'to-purchases',
  uploadParticipants = 'upload-participants-csv',
  uploadClients = 'upload-clients-csv',
  KpiFirstDay = 'kpi-first-day',
}

export interface Filters {
  summoned: boolean;
  confirmed: boolean;
  not_confirmed: boolean;
  out: boolean;
  postpone: boolean;
  checked: boolean;
  abandonment: boolean;
  enrolled: boolean;
  waiting_list: boolean;
  pending_postpone: boolean;
}

interface MixinInterface extends WithPagination, SearchMixin, ColumnOrderMixin {
}

@Component({
  components: {
    Btn,
    ConvocationRow,
    DropUp,
    GroupFilter,
    Icon,
    ItemThumb,
    Pagination,
    Spinner,
    TableColumnOrder,
    Modal,
  },
  computed: {
    ...mapGetters({
      getCountPostponeList: 'getCountPostponeList',
      getCountWaitingList: 'getCountWaitingList',
      getConvocation: 'getConvocation',
      getParticipantList: 'getParticipantList',
      getWaitingList: 'getWaitingList',
      getPostponeList: 'getPostponeList',
      isOpenNavbar: 'isOpenNavbar',
      participantMassiveEmailList: 'getParticipantMassiveEmailList',
    })
  },
  methods: {
    ...mapActions({
      uploadConvocationCSV: 'uploadConvocationCSV',
    }),
  }

})

export default class ParticipantList extends Mixins<MixinInterface>(
  WithPagination,
  SearchMixin,
  ColumnOrderMixin,
) {
  @Prop() private convocationId!: string;
  @Prop() private isLive!: boolean;
  @Prop() private parsedNextConvocationListOptions!: any[];
  @Prop() private initialNextConvocationListIndex!: number;
  @Prop() private paymentStatusOptions!: any[];
  @Prop() private columnsVisibility!: any;
  @Prop() private searchValue!: string;
  @Prop() private searchNifValue!: string;
  @Prop() private headerClass!: string;
  @Prop() private isBeforeConfirmationPeriod!: boolean;

  private uploadConvocationCSV !: any;
  private assets = Assets;
  private dropUpColorHover = DropUpColorHover;
  private iconEnum = IconEnum;
  private btnColor = BtnColor;
  private btnWidth = BtnWidth;
  private modalTitleColor = ModalTitleColor;
  private getConvocation!: any;
  private participantMassiveEmailList!: any;
  private loadingGroupFilters = true;
  private getParticipantList!: Participant[];
  private getWaitingList!: Participant[];
  private getPostponeList!: Participant[];
  private getCountPostponeList: any;
  private getCountWaitingList: any;
  private isOpenNavbar!: any;
  private lastNoteTyped: string = '';
  private interval!: any;
  private cleanSearch = false;
  private participantListLoaded = false;
  private kpiModalOpened = false;
  private kpiValues = {
    summoned: 0,
    enrolled: 0,
    confirmed: 0,
    notConfirmed: 0,
    postpone: 0,
    out: 0,
  };
  private modalWidth = ModalWidth;

  private filters: Filters = {
    summoned: true,
    confirmed: false,
    not_confirmed: false,
    out: false,
    postpone: false,
    checked: false,
    abandonment: false,
    enrolled: false,
    waiting_list: false,
    pending_postpone: false,
  };

  @Watch('searchValue')
  public watchSearchValue(newVal: any, oldVal: any) {
    this.searchString = this.searchValue;
  }

  @Watch('searchNifValue')
  public watchSearchNifValue(newVal: any, oldVal: any) {
    this.searchNifString = this.searchNifValue;
  }

  get convocation() {
    return this.getConvocation(this.convocationId);
  }

  get showPagination() {
    return this.participantListLoaded && !this.spinner.show;
  }

  get showTable() {
    const result = (this.participantListLoaded && !this.spinner.show);
    this.$emit('show-table', result);
    return result;
  }

  get lastColumnTitle() {
    return (this.isLive) ? 'Ent/Reg/Dip' : 'Ent/Dip';
  }

  get lastColumnClass() {
    return (this.isLive) ? 'table-cell--last-icons-convocation' : 'table-cell--last-icons-convocation--future';
  }

  get dropUpOption() {
    const options = [
      { value: ListActions.CountDown, name: 'Ver cuenta atrás' },
      { value: ListActions.DownloadCsv, name: 'Exportar lista' },
      { value: ListActions.uploadParticipants, name: 'Importar Participantes' },
      // {value: ListActions.uploadClients, name: 'Subir Clientes'},
      { value: ListActions.EmailToMultiple, name: 'Email masivo' },
      { value: ListActions.ToPurchases, name: 'Ir a compras' },
    ];
    if (this.isPastConvocation) {
      options.push({
        value: ListActions.KpiFirstDay,
        name: 'KPI primer día',
      });
    }
    return options;
  }

  get countersFirstDay(): CounterInterface[] {
    const counters = [
      {
        value: this.kpiValues.summoned,
        title: 'Reservas',
        active: false,
        color: CounterColor.Secondary,
        filter: 'summoned',
        visible: true,
      }, {
        value: this.kpiValues.enrolled,
        title: 'Inscritos',
        active: false,
        color: CounterColor.Primary,
        filter: 'enrolled',
        visible: true,
      }, {
        value: this.kpiValues.confirmed,
        title: 'Confirmados',
        active: false,
        color: CounterColor.Primary,
        filter: 'confirmed',
        visible: true,
      }, {
        value: this.kpiValues.notConfirmed,
        title: 'No confirmados',
        active: false,
        color: CounterColor.GreyDark,
        filter: 'not_confirmed',
        visible: true,
      }, {
        value: this.kpiValues.postpone,
        title: 'Posponen',
        active: false,
        color: CounterColor.Secondary,
        filter: 'postpone',
        visible: true,
      }, {
        value: this.kpiValues.out,
        title: 'Bajas',
        active: false,
        color: CounterColor.BlueDark,
        filter: 'out',
        visible: true,
      },
    ];

    return counters;
  }

  get counterFilters(): CounterInterface[] {
    const counters = [
      {
        value: (this.convocation) ? parseInt(this.convocation.totalParticipantEnrolled) : 0,
        title: 'Inscritos',
        active: this.filters.enrolled,
        color: CounterColor.Primary,
        filter: 'enrolled',
        visible: true,
      }, {
        value: (this.convocation) ? this.convocation.totalParticipantConfirmed : 0,
        title: 'Confirmados',
        active: this.filters.confirmed,
        color: CounterColor.Primary,
        filter: 'confirmed',
        visible: true,
      }, {
        value: (this.convocation) ? this.convocation.totalParticipantNotConfirmed : 0,
        title: 'No confirmados',
        active: this.filters.not_confirmed,
        color: CounterColor.GreyDark,
        filter: 'not_confirmed',
        visible: true,
      },
      {
        value: (this.convocation) ? this.convocation.totalParticipantChecked : 0,
        title: 'Registrados',
        active: this.filters.checked,
        color: CounterColor.Primary,
        filter: 'checked',
        visible: this.isLive,
      },
      {
        value: (this.convocation) ? this.convocation.totalParticipantPostpone : 0,
        title: 'Posponen',
        active: this.filters.postpone,
        color: CounterColor.Secondary,
        filter: 'postpone',
        visible: !this.isBeforeConfirmationPeriod,
      },
      {
        value: (this.convocation) ? this.convocation.totalParticipantAbandonment : 0,
        title: 'Abandonos',
        active: this.filters.abandonment,
        color: CounterColor.BlueDark,
        filter: 'abandonment',
        visible: this.isLive,
      },
      {
        value: (this.convocation) ? this.convocation.totalParticipantOut : 0,
        title: 'Bajas',
        active: this.filters.out,
        color: CounterColor.BlueDark,
        filter: 'out',
        visible: true,
      },
      {
        value: (this.convocation) ? this.getCountPostponeList : 0,
        title: 'Pendientes',
        active: this.filters.pending_postpone,
        color: CounterColor.Secondary,
        filter: 'pending_postpone',
        visible: this.convocation.isPendingPostponeList,
      },
      {
        value: (this.convocation) ? this.getCountWaitingList : 0,
        title: 'Espera',
        active: this.filters.waiting_list,
        color: CounterColor.Secondary,
        filter: 'waiting_list',
        visible: true,
      },
      {
        value: (this.convocation) ? this.convocation.totalParticipantSummoned : 0,
        title: 'Total',
        active: this.filters.summoned,
        color: CounterColor.Secondary,
        filter: 'summoned',
        visible: true,
      },
    ];

    return counters;
  }

  get columnApName() {
    let name = 'Ap';
    if (this.isBeforeConfirmationPeriod) {
      name = 'CC';
    } else if (this.isLive) {
      name = 'Ab';
    }

    return name;
  }

  get isPastConvocation(): boolean {
    const firstDay = dayjs(this.convocation.timetables[0].date);
    const now = dayjs();
    return firstDay.isBefore(now);
  }

  get isFirstDay(): boolean {
    let result = false;
    const now = dayjs();
    const timetables = this.convocation.timetables;
    if (timetables) {
      const firstDay = dayjs(timetables[0].date, { locale: 'es' });
      if (now.diff(firstDay, ('days' as UnitType)) === 0) {
        result = true;
      }
    }
    return result;
  }


  public fetchList() {
    this.requestShowSpinner();
    this.$emit('showing-spinner');
    this.fetchBudgetList()
    this.fetchParticipantList((res: any) => {
      this.onFetchSuccess(res);
      this.$emit('list-loaded', res);
      this.participantListLoaded = true;
      this.changeSpinnerVisibility(false);
    });
  }

  private fetchBudgetList(callback: any = () => { const doNothing = 1; },) {
    this.$store.dispatch('fetchBudgetList', {}).then((res: any) => {
      callback();
    });
  }

  public searchCallback() {
    this.fetchFirstPageList();
  }

  private created() {
    this.changeSpinnerVisibility(true);
    this.searchString = this.searchValue;
    this.searchNifString = this.searchNifValue;
    this.fetchFirstPageList();
  }

  private refetchList() {
    this.$emit('refetch-list');
  }

  private formatConvocationDates(convocation: Convocation): string {
    return ConvocationService.formatDates(
      convocation.timetables as TimetablesInterface[],
    );
  }

  // tslint:disable-next-line:member-ordering
  public columnOrderFetchList() {
    this.requestShowSpinner();
    this.fetchParticipantList(
      (res: any) => {
        this.columnOrderOnFetchSuccess();
        this.changeSpinnerVisibility(false);
      },
    );
  }

  private setKpiModalOpened(value: boolean) {
    this.kpiModalOpened = value;
  }

  private handleComeBack() {
    this.filters = {
      summoned: true,
      confirmed: false,
      not_confirmed: false,
      out: false,
      postpone: false,
      checked: false,
      abandonment: false,
      enrolled: false,
      waiting_list: false,
      pending_postpone: false
    };
    this.cleanSearch = !this.cleanSearch;
    if (this.searchString === '') {
      this.fetchFirstPageList();
    } else {
      this.searchString = '';
    }
  }

  private fetchParticipantChecked() {
    this.$store.dispatch(
      'fetchParticipantChecked',
      { convocationId: this.convocationId },
    );
  }

  private fetchParticipantList(callback: any = () => {
    const doNothing = 1;
  }) {
    this.$store.dispatch(
      'fetchParticipantList',
      {
        id: this.convocationId,
        offset: this.offset,
        limit: this.limit,
        filter: this.filters,
        ordering: this.composeOrdering(),
        search: this.searchString,
        searchNif: this.searchNifString,
      },
    ).then((res: any) => {
      this.changeSpinnerVisibility(false);
      if (callback) {
        callback(res);
      }
    })
      .catch((err) => {
        this.changeSpinnerVisibility(false);
      });
  }

  private fetchTagList() {
    this.$store.dispatch('fetchTagList');
  }

  private fetchNextConvocationList(convocationId: string) {
    this.$store.dispatch('fetchNextConvocationList', {
      id: convocationId,
    });
  }

  private fetchDelegationList(callback: any = () => {
    const doNothing = 1;
  }) {
    this.$store.dispatch('fetchDelegationList').then((res: any) => {
      callback();
    });
  }

  private fetchParticipantMassiveEmailList(callback: any = () => {
    const doNothing = 1;
  }) {
    const params = {
      search: this.searchString,
      not_confirmed: this.filters.not_confirmed,
      confirmed: this.filters.confirmed,
      out: this.filters.out,
      postpone: this.filters.postpone,
      checked: this.filters.checked,
      abandonment: this.filters.abandonment,
      team_list: false,
      waiting_list: false,
      waiting_repeater_list: false,
      pending_postpone: false
    };
    this.$store.dispatch('fetchParticipantMassiveEmail', {
      convocationId: this.convocationId,
      params,
    }).then(
      (res) => {
        callback();
      },
    );
  }

  private changeFilter(counter: CounterInterface) {
    this.filters = {
      confirmed: false,
      summoned: false,
      out: false,
      postpone: false,
      not_confirmed: false,
      checked: false,
      abandonment: false,
      enrolled: false,
      waiting_list: false,
      pending_postpone: false
    };
    switch (counter.filter) {
      case 'summoned':
        this.filters.summoned = true;
        break;
      case 'not_confirmed':
        this.filters.not_confirmed = true;
        break;
      case 'out':
        this.filters.out = true;
        break;
      case 'postpone':
        this.filters.postpone = true;
        break;
      case 'confirmed':
        this.filters.confirmed = true;
        break;
      case 'checked':
        this.filters.checked = true;
        break;
      case 'abandonment':
        this.filters.abandonment = true;
        break;
      case 'enrolled':
        this.filters.enrolled = true;
        break;
      case 'waiting_list':
        this.filters.waiting_list = true;
        break;
      case 'pending_postpone':
        this.filters.pending_postpone = true;
        break;
    }
    this.requestShowSpinner();
    this.fetchFirstPageList();
  }

  private setServerError(text: string) {
    this.$store.dispatch('setServerError', text);
  }

  private kpiSuccess(res: any) {
    this.kpiValues = res.data;
    this.setKpiModalOpened(true);
  }

  private kpiError(err: any) {
    const response = err.response;
    if (response.status === 404) {
      this.setServerError('No se han encontrado KPIs para esta convocatoria');
    } else {
      this.setServerError(Constants.internalServerErrorMessage);
    }
  }

  private retrieveConvocationFirstDayKpi() {
    ConvocationService.convocationFirstDayKpi(this.convocationId)
      .then(this.kpiSuccess, this.kpiError);
  }

  private async dropUpOptionSelected(option: any) {

    switch (option.value) {
      case ListActions.DownloadCsv:
        ConvocationService.downloadCsv(this.convocationId, this.filters);
        break;
      // case ListActions.uploadClients:
      //   const clients = await ConvocationService.uploadCsv();
      //   const clientMessage = await this.uploadConvocationCSV({
      //     file: clients,
      //     url: Endpoints.clientImport,
      //     convocation_id: this.convocationId
      //   })
      //   this.showMessage(clientMessage)
      //   break;
      case ListActions.uploadParticipants:
        const participants = await ConvocationService.uploadCsv();
        const participantMessage = await this.uploadConvocationCSV({
          file: participants,
          url: Endpoints.participantImport,
          convocation_id: this.convocationId
        })
        this.showMessage(participantMessage)
        break;
      case ListActions.EmailToMultiple:
        this.fetchParticipantMassiveEmailList(
          () => {
            ConvocationService.mailToFilteredUsers(this.participantMassiveEmailList);
          },
        );
        break;
      case ListActions.CountDown:
        const url = `/#/convocation/${this.convocation.id}/count-down`;
        window.open(url, '_blank');
        break;
      case ListActions.ToPurchases:
        this.$router.push({
          name: RouteNames.PurchaseConvocationDetail, params: {
            id: this.convocationId,
          }
        });
        break;
      case ListActions.KpiFirstDay:
        this.retrieveConvocationFirstDayKpi();
        break;
    }
  }

  private showMessage(message: string) {
    this.$emit('show-message', message);
  }

}
